import React, { useEffect, useState } from "react";
import { Plus, Trash2, Save, X, Mail, Power, PowerOff, CheckCircle2, AlertCircle, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

const GMAIL_DEFAULTS = {
    smtp_host: "smtp.gmail.com",
    smtp_port: 587,
    imap_host: "imap.gmail.com",
    imap_port: 993,
};

const blankForm = {
    name: "",
    smtp_user: "",
    smtp_password: "",
    smtp_from: "",
    smtp_host: GMAIL_DEFAULTS.smtp_host,
    smtp_port: GMAIL_DEFAULTS.smtp_port,
    imap_host: GMAIL_DEFAULTS.imap_host,
    imap_port: GMAIL_DEFAULTS.imap_port,
    imap_user: "",
    imap_password: "",
};

const QUICK_TEMPLATES = [
    { label: "Accounts (accounts@reddogfoundation.org)", email: "accounts@reddogfoundation.org", name: "Accounts" },
    { label: "Sales (sales@reddogfoundation.org)", email: "sales@reddogfoundation.org", name: "Sales" },
];

const MailAccountsPanel = () => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [adding, setAdding] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(blankForm);
    const [testing, setTesting] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/mail-accounts");
            setRows(data || []);
        } catch (e) {
            toast.error("Could not load mail accounts");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const reset = () => { setAdding(false); setEditingId(null); setForm(blankForm); };

    const startEdit = (a) => {
        setEditingId(a.id);
        setAdding(false);
        setForm({
            name: a.name,
            smtp_host: a.smtp_host || GMAIL_DEFAULTS.smtp_host,
            smtp_port: a.smtp_port || GMAIL_DEFAULTS.smtp_port,
            smtp_user: a.smtp_user || "",
            smtp_password: "",  // never pre-populate password
            smtp_from: a.smtp_from || "",
            imap_host: a.imap_host || GMAIL_DEFAULTS.imap_host,
            imap_port: a.imap_port || GMAIL_DEFAULTS.imap_port,
            imap_user: a.imap_user || "",
            imap_password: "",
        });
    };

    const applyTemplate = (tpl) => {
        setForm((p) => ({
            ...p,
            name: tpl.name,
            smtp_user: tpl.email,
            imap_user: tpl.email,
            smtp_from: tpl.email,
        }));
    };

    const save = async () => {
        if (!form.name.trim() || !form.smtp_user.trim() || (!editingId && !form.smtp_password)) {
            toast.error("Name, email, and password are required.");
            return;
        }
        try {
            const payload = { ...form };
            // For edits, drop empty password so we keep the existing one
            if (editingId) {
                if (!payload.smtp_password) delete payload.smtp_password;
                if (!payload.imap_password) delete payload.imap_password;
                await api.patch(`/admin/mail-accounts/${editingId}`, payload);
            } else {
                await api.post("/admin/mail-accounts", payload);
            }
            toast.success(editingId ? "Mail account saved" : "Mail account created");
            reset();
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        }
    };

    const remove = async (a) => {
        if (!window.confirm(`Delete mail account "${a.name}"?`)) return;
        try {
            await api.delete(`/admin/mail-accounts/${a.id}`);
            toast.success("Deleted");
            await load();
        } catch (e) {
            toast.error("Delete failed");
        }
    };

    const activate = async (a) => {
        try {
            await api.post(`/admin/mail-accounts/${a.id}/activate`);
            toast.success(`"${a.name}" is now the active mailbox`);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Activate failed");
        }
    };

    const deactivate = async (a) => {
        try {
            await api.post(`/admin/mail-accounts/${a.id}/deactivate`);
            toast.success("Mailbox deactivated (system mail disabled)");
            await load();
        } catch (e) {
            toast.error("Deactivate failed");
        }
    };

    const testNow = async (a) => {
        setTesting(a.id);
        try {
            const { data } = await api.post(`/admin/mail-accounts/${a.id}/test`);
            if (data.smtp.ok && data.imap.ok) {
                toast.success(`SMTP + IMAP connected for "${a.name}"`);
            } else if (data.smtp.ok || data.imap.ok) {
                toast.warning(`Partial: SMTP=${data.smtp.ok ? "OK" : data.smtp.reason}, IMAP=${data.imap.ok ? "OK" : data.imap.reason}`);
            } else {
                toast.error(`Test failed — SMTP: ${data.smtp.reason}; IMAP: ${data.imap.reason}`);
            }
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Test failed");
        } finally {
            setTesting(null);
        }
    };

    const sendTest = async (a) => {
        if (!window.confirm(`Send a test message to ${a.smtp_from || a.smtp_user}?`)) return;
        try {
            const { data } = await api.post(`/admin/mail-accounts/${a.id}/send-test`);
            toast.success(`Test message sent to ${data.to}`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Send failed");
        }
    };

    return (
        <div className="space-y-6" data-testid="mail-accounts-panel">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
                <div>
                    <h2 className="font-heading text-2xl text-[#1C252E] tracking-tight">Mailboxes</h2>
                    <p className="text-sm text-[#4A5568] mt-1 max-w-2xl">
                        Manage Gmail / Google Workspace mailboxes for the foundation. <strong>Exactly one is active at a time</strong> — flip between Accounts and Sales with one click. The active mailbox is what sends donor receipts, contractor notifications, and feeds the admin Mailbox panel.
                    </p>
                </div>
                <button onClick={() => { setAdding(true); setEditingId(null); setForm(blankForm); }} data-testid="mail-account-add-btn" className="inline-flex items-center gap-2 bg-[#E53935] hover:bg-[#D32F2F] text-white rounded-full px-5 py-2.5 text-sm font-medium transition">
                    <Plus className="w-4 h-4" strokeWidth={2} /> Add mailbox
                </button>
            </div>

            <div className="bg-[#F4F1EA]/60 border border-[#E53935]/10 rounded-2xl p-4 text-xs text-[#4A5568] space-y-1.5">
                <p className="text-[#1C252E] font-bold uppercase tracking-wider text-[10px]">Gmail / Google Workspace setup</p>
                <p>1. Sign in to <strong>{`{username}@reddogfoundation.org`}</strong> → Google Account → Security → enable <strong>2-Step Verification</strong>.</p>
                <p>2. Under Security → <strong>App passwords</strong> → generate a 16-character app password for &quot;Mail&quot;.</p>
                <p>3. Paste that app password below (NOT your regular Google password). Hosts default to <code>smtp.gmail.com:587</code> and <code>imap.gmail.com:993</code> — no need to change them.</p>
            </div>

            {(adding || editingId) && (
                <div className="bg-white border border-[#E53935]/30 rounded-2xl p-5 space-y-3" data-testid="mail-account-form">
                    {adding && (
                        <div className="flex flex-wrap gap-2 pb-2 border-b border-black/5">
                            <span className="text-[10px] uppercase tracking-wider font-bold text-[#4A5568] mr-2 self-center">Quick fill:</span>
                            {QUICK_TEMPLATES.map((tpl) => (
                                <button key={tpl.email} type="button" onClick={() => applyTemplate(tpl)} data-testid={`mail-account-template-${tpl.name.toLowerCase()}`} className="text-xs px-3 py-1 rounded-full border border-[#E53935]/30 text-[#E53935] hover:bg-[#E53935]/5">
                                    {tpl.label}
                                </button>
                            ))}
                        </div>
                    )}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <label className="text-xs uppercase tracking-wider font-bold text-[#4A5568]">Label *</label>
                            <input data-testid="mail-account-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 w-full rounded-xl border border-black/10 px-4 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]/40 focus:border-[#E53935]" placeholder="Accounts" />
                        </div>
                        <div>
                            <label className="text-xs uppercase tracking-wider font-bold text-[#4A5568]">Email address *</label>
                            <input data-testid="mail-account-email-input" type="email" value={form.smtp_user} onChange={(e) => setForm({ ...form, smtp_user: e.target.value, imap_user: form.imap_user || e.target.value, smtp_from: form.smtp_from || e.target.value })} className="mt-1 w-full rounded-xl border border-black/10 px-4 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]/40 focus:border-[#E53935]" placeholder="accounts@reddogfoundation.org" />
                        </div>
                    </div>
                    <div>
                        <label className="text-xs uppercase tracking-wider font-bold text-[#4A5568]">Google App Password {editingId ? "(leave blank to keep current)" : "*"}</label>
                        <input data-testid="mail-account-password-input" type="password" value={form.smtp_password} onChange={(e) => setForm({ ...form, smtp_password: e.target.value })} className="mt-1 w-full rounded-xl border border-black/10 px-4 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]/40 focus:border-[#E53935] font-mono" placeholder="xxxx xxxx xxxx xxxx" />
                        <p className="text-[11px] text-[#4A5568] mt-1">Generate at: Google Account → Security → App passwords. Spaces optional.</p>
                    </div>
                    <details className="text-sm">
                        <summary className="cursor-pointer text-[#4A5568] hover:text-[#1C252E] font-medium">Advanced (host/port overrides)</summary>
                        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                                <label className="text-xs uppercase tracking-wider font-bold text-[#4A5568]">SMTP host</label>
                                <input data-testid="mail-account-smtp-host-input" value={form.smtp_host} onChange={(e) => setForm({ ...form, smtp_host: e.target.value })} className="mt-1 w-full rounded-xl border border-black/10 px-4 py-2.5 bg-white text-sm font-mono" />
                            </div>
                            <div>
                                <label className="text-xs uppercase tracking-wider font-bold text-[#4A5568]">SMTP port</label>
                                <input type="number" data-testid="mail-account-smtp-port-input" value={form.smtp_port} onChange={(e) => setForm({ ...form, smtp_port: parseInt(e.target.value, 10) || 587 })} className="mt-1 w-full rounded-xl border border-black/10 px-4 py-2.5 bg-white text-sm" />
                            </div>
                            <div>
                                <label className="text-xs uppercase tracking-wider font-bold text-[#4A5568]">IMAP host</label>
                                <input data-testid="mail-account-imap-host-input" value={form.imap_host} onChange={(e) => setForm({ ...form, imap_host: e.target.value })} className="mt-1 w-full rounded-xl border border-black/10 px-4 py-2.5 bg-white text-sm font-mono" />
                            </div>
                            <div>
                                <label className="text-xs uppercase tracking-wider font-bold text-[#4A5568]">IMAP port</label>
                                <input type="number" data-testid="mail-account-imap-port-input" value={form.imap_port} onChange={(e) => setForm({ ...form, imap_port: parseInt(e.target.value, 10) || 993 })} className="mt-1 w-full rounded-xl border border-black/10 px-4 py-2.5 bg-white text-sm" />
                            </div>
                        </div>
                    </details>
                    <div className="flex items-center justify-end gap-2 pt-2">
                        <button onClick={reset} className="text-sm text-[#4A5568] hover:text-[#1C252E] inline-flex items-center gap-1"><X className="w-4 h-4" strokeWidth={2} /> Cancel</button>
                        <button onClick={save} data-testid="mail-account-save-btn" className="inline-flex items-center gap-1 bg-[#E53935] hover:bg-[#D32F2F] text-white rounded-full px-5 py-2 text-sm font-medium"><Save className="w-4 h-4" strokeWidth={2} /> {editingId ? "Save changes" : "Add mailbox"}</button>
                    </div>
                </div>
            )}

            {loading ? (
                <p className="text-sm text-[#4A5568]">Loading…</p>
            ) : rows.length === 0 ? (
                <p className="text-sm text-[#4A5568] italic" data-testid="mail-accounts-empty">No mailboxes yet — add one to enable system email.</p>
            ) : (
                <ul className="space-y-3" data-testid="mail-accounts-list">
                    {rows.map((a) => {
                        const tested = a.last_test_result;
                        return (
                            <li key={a.id} data-testid={`mail-account-row-${a.id}`} className={`p-4 rounded-2xl border ${a.active ? "bg-white border-[#E53935]/40 ring-1 ring-[#E53935]/20" : "bg-white border-black/10"}`}>
                                <div className="flex items-start justify-between gap-3 flex-wrap">
                                    <div className="flex items-start gap-3 min-w-0 flex-1">
                                        <span className={`inline-flex items-center justify-center w-9 h-9 rounded-full shrink-0 ${a.active ? "bg-[#E53935] text-white" : "bg-[#F4F1EA] text-[#4A5568]"}`}><Mail className="w-4 h-4" strokeWidth={2} /></span>
                                        <div className="min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <p className="font-medium text-[#1C252E] truncate">{a.name}</p>
                                                {a.active && <span className="text-[10px] uppercase tracking-wider font-bold bg-[#E53935] text-white px-2 py-0.5 rounded-full">Active</span>}
                                                {tested && (
                                                    tested.smtp_ok && tested.imap_ok ? <span className="text-[10px] uppercase tracking-wider font-bold bg-green-100 text-green-800 px-2 py-0.5 rounded-full inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Tested OK</span>
                                                    : <span className="text-[10px] uppercase tracking-wider font-bold bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full inline-flex items-center gap-1"><AlertCircle className="w-3 h-3" /> Test failed</span>
                                                )}
                                            </div>
                                            <p className="text-xs text-[#4A5568] truncate font-mono">{a.smtp_user}</p>
                                            <p className="text-[10px] text-[#4A5568] mt-0.5">
                                                Password: {a.smtp_password_set ? a.smtp_password_masked : <em>not set</em>}
                                                {a.last_tested_at && <> · Tested {new Date(a.last_tested_at).toLocaleString()}</>}
                                            </p>
                                            {tested && (!tested.smtp_ok || !tested.imap_ok) && (
                                                <p className="text-[10px] text-amber-800 mt-0.5">{tested.smtp_ok ? "" : `SMTP: ${tested.smtp_reason}. `}{tested.imap_ok ? "" : `IMAP: ${tested.imap_reason}`}</p>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-1 flex-wrap">
                                        {a.active ? (
                                            <button onClick={() => deactivate(a)} data-testid={`mail-account-deactivate-${a.id}`} className="inline-flex items-center gap-1 text-xs bg-[#1C252E] hover:bg-black text-white rounded-full px-3 py-1.5 font-medium">
                                                <PowerOff className="w-3.5 h-3.5" strokeWidth={2} /> Deactivate
                                            </button>
                                        ) : (
                                            <button onClick={() => activate(a)} data-testid={`mail-account-activate-${a.id}`} className="inline-flex items-center gap-1 text-xs bg-green-600 hover:bg-green-700 text-white rounded-full px-3 py-1.5 font-medium">
                                                <Power className="w-3.5 h-3.5" strokeWidth={2} /> Make active
                                            </button>
                                        )}
                                        <button onClick={() => testNow(a)} disabled={testing === a.id} data-testid={`mail-account-test-${a.id}`} className="inline-flex items-center gap-1 text-xs bg-white hover:bg-[#F4F1EA] text-[#1C252E] border border-black/10 rounded-full px-3 py-1.5 font-medium disabled:opacity-50">
                                            {testing === a.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />} Test
                                        </button>
                                        <button onClick={() => sendTest(a)} data-testid={`mail-account-send-test-${a.id}`} className="inline-flex items-center gap-1 text-xs bg-white hover:bg-[#F4F1EA] text-[#1C252E] border border-black/10 rounded-full px-3 py-1.5 font-medium" title="Send a test message to this mailbox">
                                            <Send className="w-3.5 h-3.5" /> Send test
                                        </button>
                                        <button onClick={() => startEdit(a)} data-testid={`mail-account-edit-${a.id}`} className="p-1.5 rounded-full text-[#4A5568] hover:bg-[#F4F1EA]" aria-label="Edit">
                                            <Mail className="w-4 h-4" strokeWidth={1.5} />
                                        </button>
                                        <button onClick={() => remove(a)} data-testid={`mail-account-delete-${a.id}`} className="p-1.5 rounded-full text-[#4A5568] hover:text-[#E53935] hover:bg-[#E53935]/10" aria-label="Delete">
                                            <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                                        </button>
                                    </div>
                                </div>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
};

export default MailAccountsPanel;
